import FilePicker from '@/components/atoms/FilePicker';
import { CONTENT_THUMBNAIL_MIN_HEIGHT, CONTENT_THUMBNAIL_MIN_WIDTH } from '@/constants/contentLimits';
import { ContentTypeTag, DomainIconTiles } from '@/modules/content/common/contentDisplay';
import { TContentDomain, TContentType } from '@/types/Content.types';
import { Box, Flex, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { ImagePlus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

type Props = {
  type: TContentType;
  title: string;
  description?: string;
  domains?: TContentDomain[];
  existingUrl?: string | null;
  file: File | null;
  onFileChange: (file: File | null) => void;
  // Read-only mode (e.g. the preview step): no click-to-upload, no remove, no controls.
  readOnly?: boolean;
};

// Branded thumbnail card: a gradient placeholder with the type tag, domain badges,
// title, "MyOmnia" brand and description overlaid, replaced by the chosen image with
// the same overlay. Clicking the card opens the file picker (unless readOnly).
const BrandedThumbnailPreview = ({
  type,
  title,
  description,
  domains = [],
  existingUrl,
  file,
  onFileChange,
  readOnly = false,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Derive a displayable URL straight from the picked file, so the card renders the
  // image even without the FilePicker preview (e.g. the read-only preview step).
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setFileUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const image = preview || fileUrl || existingUrl || undefined;
  const hasImage = Boolean(image);

  const openPicker = () => inputRef.current?.click();

  const removeImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileChange(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <VStack align="stretch" gap="8px" width="400px">
      <Box
        position="relative"
        height="400px"
        borderRadius="14px"
        overflow="hidden"
        cursor={readOnly ? 'default' : 'pointer'}
        onClick={readOnly ? undefined : openPicker}
        backgroundImage={
          hasImage ? undefined : 'linear-gradient(180deg, #4E1AEF 0%, #835FF4 20%, #CABAFA 40%, #FFFFFF 60%)'
        }
        backgroundSize="cover"
        backgroundPosition="center"
      >
        {/* Real <img> layer — renders both data URLs (create preview) and remote URLs. */}
        {hasImage && (
          <Image src={image} alt={title} position="absolute" inset={0} width="100%" height="100%" objectFit="cover" />
        )}

        {/* Readability scrim over images — semi-transparent so the image stays visible. */}
        {hasImage && (
          <Box
            position="absolute"
            inset={0}
            backgroundImage="linear-gradient(180deg, #4E1AEF 0%, rgba(78, 26, 239, 0) 100%)"
          />
        )}

        {/* All overlay content lives in one padded container. */}
        <Flex position="absolute" inset={0} direction="column" padding="24px" gap="0px">
          {/* Top: type tag + domain badges, then title + brand + description */}
          <VStack align="flex-start" gap={'8px'} flex={1} minWidth={0}>
            <HStack justifyContent={'space-between'} width={'full'}>
              <ContentTypeTag type={type} />
              {domains.length > 0 && (
                <Box flexShrink={0}>
                  <DomainIconTiles domains={domains} />
                </Box>
              )}
            </HStack>

            <Text fontSize="22px" fontWeight={600} color="white" width="100%" fontFamily={'Lora'} fontStyle={'italic'}>
              {title || 'Title will go here'}
            </Text>
            <Text fontSize="14px" fontWeight={700} color="whiteAlpha.800">
              MyOmnia
            </Text>

            {description && (
              <Text fontSize="14px" color="white" fontWeight={600} noOfLines={5}>
                {description}
              </Text>
            )}
          </VStack>

          {/* Upload affordance (editable + no image) fills the remaining space below */}
          {!hasImage && !readOnly && (
            <VStack flex={1} justify="end" gap="4px" marginBottom={'30px'}>
              <Box color="text.mediumGray">
                <ImagePlus size={60} strokeWidth={1.8} />
              </Box>
              <Text fontSize="13px" fontWeight={800} color="text.mediumGray" textTransform="uppercase">
                Upload thumbnail
              </Text>
              <Text fontSize="11px" color="text.mediumGray">
                PNG, JPG, HEIC · Max 5MB
              </Text>
            </VStack>
          )}
        </Flex>
      </Box>

      {/* Controls below the card — only in editable mode */}
      {!readOnly && (
        <VStack justify="space-between" align="center" gap="8px">
          <Text fontSize="12px" color="text.mediumGray">
            Note: This content will be visible to all organizations.
          </Text>
          {hasImage && (
            <Text
              fontSize="12px"
              fontWeight={700}
              color="extra.red"
              cursor="pointer"
              flexShrink={0}
              whiteSpace="nowrap"
              _hover={{ textDecoration: 'underline' }}
              onClick={removeImage}
            >
              Remove thumbnail
            </Text>
          )}
        </VStack>
      )}

      {!readOnly && (
        <FilePicker
          inputRef={inputRef}
          selectedFile={file}
          setSelectedFile={onFileChange}
          setPreview={setPreview}
          accept="image/*"
          fileMinWidthPx={CONTENT_THUMBNAIL_MIN_WIDTH}
          fileMinHeightPx={CONTENT_THUMBNAIL_MIN_HEIGHT}
          fileSizeLimitInMBs={5}
        />
      )}
    </VStack>
  );
};

export default BrandedThumbnailPreview;
